import { Link } from 'react-router-dom';
import { ChevronLeft, ArrowRight } from 'lucide-react';
import { useGeo } from '../contexts/GeoContext';
import { card as optCard, hero as optHero } from '../lib/imageUrl';

const BRANDS = [
  {
    name: 'TIMBERLAND',
    tagline: 'Built to Last',
    image: 'https://media.finishline.com/i/finishline/10061713_231_P1?w=600&h=600&fmt=auto',
    path: '/products?category=footwear&brand=timberland',
    bg: '#FDF0D5',
  },
  {
    name: 'NIKE',
    tagline: 'Just Do It',
    image: 'https://media.finishline.com/i/finishline/CW2288_111_P1?w=600&h=600&fmt=auto',
    path: '/products?category=footwear&brand=nike',
    bg: '#FAD4D4',
  },
];

// 🥾 BEAMS x Timberland Buckle GORE-TEX Boot 'Wheat' — 2026 drop hero
// Retail $260 USD → +10% Doe Wiz markup = $286 MSRP
const BEAMS_HERO = {
  title: "BEAMS × TIMBERLAND",
  subtitle: "Buckle GORE-TEX Boot — Wheat",
  description:
    "Tokyo's legendary BEAMS joins Timberland for a buckle-strap, GORE-TEX lined 6-inch boot on the iconic Wheat Nubuck. Designed by Nathan Swartz. Dropped April 2026. Limited stock.",
  retail: 260,
  price: 286, // 260 × 1.10
  image:
    'https://customer-assets.emergentagent.com/job_9b5e85e7-cfb4-4ed2-9bc1-5a96c527aff2/artifacts/y9cvy4ii_TB0A5NGSEYJ.png.png.webp',
  ctaLink: '/products?category=footwear&brand=timberland&search=BEAMS',
};

export default function FootwearPage() {
  const { formatPrice } = useGeo();

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8" data-testid="footwear-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button onClick={() => window.history.back()} className="inline-flex items-center text-sm font-semibold mb-6 hover:underline" data-testid="back-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </button>

        <div className="mb-8">
          <span className="text-sm uppercase tracking-[0.2em] font-semibold text-[#525252]">Shop By Brand</span>
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black tracking-tight mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
            FOOTWEAR
          </h1>
        </div>

        {/* 🥾 BEAMS × TIMBERLAND HERO — Limited April 2026 Drop */}
        <Link
          to={BEAMS_HERO.ctaLink}
          data-testid="beams-hero"
          className="block border-2 border-[#0A0A0A] bg-[#F5EEDC] overflow-hidden card-brutalist mb-8 group"
        >
          <div className="grid grid-cols-1 md:grid-cols-5 items-center">
            <div className="md:col-span-3 aspect-[4/3] md:aspect-auto md:h-[380px] bg-[#F5EEDC] overflow-hidden relative">
              <img
                src={optHero(BEAMS_HERO.image)}
                alt="BEAMS x Timberland Buckle GORE-TEX Boot Wheat"
                className="w-full h-full object-contain p-6 transition-transform duration-700 group-hover:scale-105"
              />
              {/* NEW DROP corner tag */}
              <span className="absolute top-3 left-3 bg-[#0A0A0A] text-[#FFC700] text-[10px] font-black uppercase tracking-[0.25em] px-3 py-1.5 shadow-[3px_3px_0px_#FFC700]">
                Just Dropped
              </span>
              <span className="absolute bottom-3 left-3 bg-[#D32027] text-white text-[10px] font-black uppercase tracking-[0.25em] px-2 py-1">
                Limited
              </span>
            </div>
            <div className="md:col-span-2 p-6 md:p-10 border-t-2 md:border-t-0 md:border-l-2 border-[#0A0A0A] bg-[#FDFBF7]">
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#525252] mb-2" data-testid="beams-eyebrow">Collaboration · Apr 2026</p>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="beams-title">
                {BEAMS_HERO.title}
              </h2>
              <p className="text-sm font-semibold mt-1 text-[#0A0A0A]">{BEAMS_HERO.subtitle}</p>
              <p className="text-xs text-[#525252] leading-relaxed mt-3" data-testid="beams-desc">
                {BEAMS_HERO.description}
              </p>
              <div className="mt-5 flex items-end gap-3">
                <span className="text-3xl font-black text-[#0A0A0A] tabular-nums" data-testid="beams-price">
                  {formatPrice(BEAMS_HERO.price)}
                </span>
                <span className="text-xs text-[#525252] line-through tabular-nums pb-1" data-testid="beams-retail">
                  {formatPrice(BEAMS_HERO.retail)} RRP
                </span>
              </div>
              <div className="mt-5 inline-flex items-center gap-2 bg-[#0A0A0A] text-white text-sm font-bold uppercase tracking-wider px-4 py-3 group-hover:bg-[#D32027] transition-colors">
                Shop the Drop <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>
        </Link>

        <div className="grid grid-cols-2 gap-3 sm:gap-6">
          {BRANDS.map((brand) => (
            <Link
              key={brand.name}
              to={brand.path}
              data-testid={`brand-${brand.name.toLowerCase()}`}
              className="relative border-2 border-[#0A0A0A] overflow-hidden group card-brutalist"
            >
              <div className="aspect-square overflow-hidden" style={{ backgroundColor: brand.bg }}>
                <img
                  src={optCard(brand.image)}
                  alt={brand.name}
                  className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105 p-4 sm:p-8"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-[#0A0A0A] px-3 sm:px-6 py-2 sm:py-4">
                <h2 className="text-white text-lg sm:text-2xl lg:text-3xl font-black tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {brand.name}
                </h2>
                <p className="text-white/60 text-xs sm:text-sm">{brand.tagline}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
